import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MediaImage from "../../../components/MediaImage";
import { deleteMedia, listMedia } from "../../../services/api/contentAdminService";
import { ERROR_CODES } from "../../../services/errors/ApiError";
import { isAuthError, isCanceledError } from "../../../services/errors/errorHandler";
import AdminHeader from "../AdminHeader";
import { CONTENT_HOME_PATH, getCollection } from "./collections";
import { AltEditor, MediaUploader, formatBytes } from "./MediaFields";
import "../admin.css";
import "./content.css";

/**
 * "Photos and files" (A.13): space used, upload, each picture's description,
 * where it's used, and Delete (refused by the server while it's in use).
 */

/** 'grounding', '54321-grounding' -> 'Grounding graphics: 54321 grounding' */
export const describeUse = ({ collection, slug }) => {
  const meta = getCollection(collection);
  return `${meta ? meta.label : collection}: ${String(slug || "").replace(/-/g, " ")}`;
};

function MediaCard({ media, onChanged, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isImage = media.kind === "image";
  const name = isImage ? media.alt || "Picture with no description" : media.filename;
  const used = media.used_by || [];

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await deleteMedia(media.id);
      onDeleted(media);
    } catch (err) {
      setBusy(false);
      setConfirming(false);
      if (isAuthError(err)) return;
      setError(
        err.code === ERROR_CODES.MEDIA_IN_USE
          ? `${err.message} Change those first, then delete it here.`
          : err.message || "Could not delete it. Please try again."
      );
    }
  };

  return (
    <li className="admin-booking cms-media-card" data-media-id={media.id}>
      {isImage ? (
        <div className="cms-preview">
          <MediaImage media={media} sizes="(max-width: 600px) 100vw, 300px" />
        </div>
      ) : (
        <p className="cms-file-name">PDF: {media.filename}</p>
      )}
      <p className="cms-hint">{formatBytes(media.size_bytes)}</p>
      {isImage && !editing && (
        <p className="cms-media-card__alt">
          <strong>Description:</strong> {media.alt || "none yet"}
        </p>
      )}
      <p className="cms-hint">
        {used.length ? `Used on: ${used.map(describeUse).join("; ")}` : "Not used on the site."}
      </p>

      {editing && (
        <AltEditor
          media={media}
          idPrefix={`cms-media-${media.id}`}
          onSaved={(updated) => {
            onChanged(updated);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      )}

      {error && <p className="admin-error" role="alert">{error}</p>}

      {confirming ? (
        <div className="admin-panel admin-panel--danger" role="group" aria-label="Confirm delete">
          <p className="admin-panel__title">Delete this {isImage ? "picture" : "PDF"} for good?</p>
          <p className="admin-panel__detail">This can’t be undone from here.</p>
          <div className="admin-actions">
            <button type="button" className="admin-btn admin-btn--danger" onClick={remove} disabled={busy}>
              {busy ? "Deleting…" : "Yes, delete it"}
            </button>
            <button type="button" className="admin-btn" onClick={() => setConfirming(false)} disabled={busy}>
              Keep it
            </button>
          </div>
        </div>
      ) : (
        !editing && (
          <div className="admin-actions">
            {isImage && (
              <button type="button" className="admin-btn" onClick={() => setEditing(true)} aria-label={`Edit description: ${name}`}>
                Edit description
              </button>
            )}
            <button
              type="button"
              className="admin-btn admin-btn--danger"
              onClick={() => {
                setError("");
                setConfirming(true);
              }}
              aria-label={`Delete: ${name}`}
            >
              Delete
            </button>
          </div>
        )
      )}
    </li>
  );
}

export default function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    listMedia({ signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setMedia(data.media || []);
        setQuota(data.quota || null);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        if (!isAuthError(err)) setError(err.message || "Could not load your photos and files.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  const used = quota ? quota.used_bytes : 0;
  const limit = quota ? quota.limit_bytes : 0;
  const percent = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <div className="admin">
      <AdminHeader />
      <main id="main" className="admin-main cms" tabIndex={-1}>
        <Link className="cms-back" to={CONTENT_HOME_PATH}>‹ Site content</Link>
        <h1>Photos and files</h1>

        {quota && (
          <div className="cms-quota">
            <p className="cms-quota__text" data-testid="quota-text">
              {formatBytes(used)} of {formatBytes(limit)} used
            </p>
            <div className="cms-quota__bar" aria-hidden="true">
              <span style={{ width: `${percent}%` }} />
            </div>
          </div>
        )}

        <div aria-live="polite" className="admin-live">
          {notice && <p className="admin-notice">{notice}</p>}
        </div>

        {uploading ? (
          <section className="cms-section" aria-label="Upload">
            <MediaUploader
              kind="any"
              idPrefix="cms-library-upload"
              onUploaded={(created) => {
                setMedia((prev) => [{ used_by: [], ...created }, ...prev]);
                setQuota((q) => (q ? { ...q, used_bytes: q.used_bytes + (created.size_bytes || 0) } : q));
                setUploading(false);
                setNotice("Uploaded. Pick it from the library when you edit a page item.");
              }}
            />
            <button type="button" className="admin-btn" onClick={() => setUploading(false)}>Cancel</button>
          </section>
        ) : (
          <button type="button" className="admin-btn admin-btn--primary" onClick={() => { setNotice(""); setUploading(true); }}>
            + Upload a picture or PDF
          </button>
        )}

        <section aria-label="Your photos and files" aria-busy={loading}>
          {loading && <p className="admin-muted" role="status">Loading…</p>}
          {!loading && error && (
            <div className="admin-error" role="alert">
              <p>{error}</p>
              <button type="button" className="admin-btn" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
            </div>
          )}
          {!loading && !error && media.length === 0 && <p className="admin-empty">Nothing uploaded yet.</p>}
          {!loading && !error && media.length > 0 && (
            <ul className="admin-list cms-media-list">
              {media.map((m) => (
                <MediaCard
                  key={m.id}
                  media={m}
                  onChanged={(updated) => setMedia((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
                  onDeleted={(gone) => {
                    setMedia((prev) => prev.filter((x) => x.id !== gone.id));
                    setQuota((q) => (q ? { ...q, used_bytes: Math.max(0, q.used_bytes - (gone.size_bytes || 0)) } : q));
                    setNotice("Deleted.");
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
